import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import * as PR from "../../prime-react/index";
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { getLocations, getPlayers, getSports, geteSportsTitles, getTournament, getTournamentLevels } from '../../services/api'
import { useDispatch, useSelector } from "react-redux";
import { aletFormForValidation, descriptionCount, displayStatesForSelectedCountries, fileTypesConfig, fileUploadLimit, getPlainText, getTimeZoneOptions, handleNumericInput, maxNumberOfFiles, textSanitize, trimFileName, validationMessages } from "../../utils/reuse";
import { RAW_FILTER_DATA } from '../common';
import { isEqual } from "lodash";
import { ErrorMessage, useToast } from "../../context/ToastContext";
import moment from 'moment-timezone';

const EditAlertForm = ({ initialData, handleBack, handleNext, backLabel, nextLabel, showError, setshowError, responseID, alertLoading, errorId, type }) => {
    const { showToast } = useToast();
    const dispatch = useDispatch();
    const isMember = useSelector((state) => state.auth.role) === "member";
    const isAnalyst = useSelector((state) => state.auth.role) === "analyst";
    const sessionData = useSelector(state => state.auth.sessionData);
    const headers = useMemo(() => {
        return {
            sessionid: sessionData.sessionId,
            'Content-Type': 'multipart/form-data'
        };
    }, [sessionData.sessionId]);

    const alerts = RAW_FILTER_DATA.alertTypeFilter;
    const channels = RAW_FILTER_DATA.channels;
    const betTimeOptions = RAW_FILTER_DATA.timeOfBet;
    const betsStateOptions = RAW_FILTER_DATA.stateOfBetsFilter;
    const confirmOptions = RAW_FILTER_DATA.confirmOptions;
    const alertsRaised = RAW_FILTER_DATA.alertsRaised;
    const customerTypeOptions = RAW_FILTER_DATA.customerTypes;
    const gender = RAW_FILTER_DATA.gender;

    const [events, setEvents] = useState([]);
    const [sports, setSports] = useState([]);
    const [eSportTitles, seteSportTitles] = useState([]);
    const [location, setLocation] = useState([]);
    const [tournamentLevel, setTournamentLevel] = useState([]);
    const [filteredTournaments, setFilteredTournaments] = useState([]);
    const [deletedAttachments, setDeletedAttachments] = useState([]);
    let fileUploadRef = useRef(null);  //file ref

    const [maxBets, setMaxBets] = useState(false);
    const [alertTime, setAlertTime] = useState('');
    const [athleteTeamTagList, setAthleteTeamTagList] = useState([]);
    const [marketTagList, setMarketTagList] = useState([]);
    const intialAlertTags = useMemo(() => initialData?.tags ?? [], [initialData?.tags]);
    const [selectedAthleteTeamTags, setSelectedAthleteTeamTags] = useState([]);
    const [selectedMarketTags, setSelectedMarketTags] = useState([]);
    const [tagListShow, setTagListShow] = useState(false);

    const athleteInitializedRef = useRef(false);
    const marketInitializedRef = useRef(false);

    useEffect(() => {
        athleteInitializedRef.current = false;
        marketInitializedRef.current = false;
    }, [initialData]);

    useEffect(() => {
        if (!athleteInitializedRef.current && athleteTeamTagList.length > 0) {
            const athleteTags = intialAlertTags.filter(t => athleteTeamTagList.some(at => at.id === t.id));
            setSelectedAthleteTeamTags(athleteTags);
            athleteInitializedRef.current = true;
        }
    }, [intialAlertTags, athleteTeamTagList]);

    useEffect(() => {
        if (!marketInitializedRef.current && marketTagList.length > 0) {
            const marketTags = intialAlertTags.filter(t => marketTagList.some(mt => mt.id === t.id));
            setSelectedMarketTags(marketTags);
            marketInitializedRef.current = true;
        }
    }, [intialAlertTags, marketTagList]);

    const hasTagsChanged = useMemo(() => {
        const initialIds = [...intialAlertTags].map(t => t.id).sort();
        const selectedIds = [...selectedAthleteTeamTags, ...selectedMarketTags].map(t => t.id).sort();
        return !isEqual(initialIds, selectedIds);
    }, [intialAlertTags, selectedAthleteTeamTags, selectedMarketTags]);
    const [timezones, setTimezones] = useState([]);
    
    
    const [initialValues, setInitialValues] = useState({
        alertType: null,
        numOfEvents: '',
        events: [],
        channel: [],
        betsState: [],
        betTime: [],
        maxBets: '',
        alertTime: '',
        customerType: [],
        customerDetails: [],
        newCustomer: { count: '', country: '' },
        description: '',
        attachments: null,
    })
    const validationSchema = (values) => {
        let schema = Yup.object().shape({
            alertType: Yup.object().nullable().required('Alert type required'),
            numOfEvents: Yup.number().required('Number of events required')
                .min(1, 'Number of events must be greater than or equal to 1')
                .max(10, 'Number of events must be less than or equal to 10'),
            channel: Yup.array().when(`isAnalyst`, {
                is: () => !isAnalyst,
                then: () => Yup.array().min(1, 'At least one channel must be selected'),
                otherwise: () => Yup.array().nullable(),
            }),
            betsState: Yup.array().when(`isAnalyst`, {
                is: () => !isAnalyst,
                then: () => Yup.array().min(1, 'At least one state of bets must be selected'),
                otherwise: () => Yup.array().nullable(),
            }),
            betTime: Yup.array().when(`isAnalyst`, {
                is: () => !isAnalyst,
                then: () => Yup.array().min(1, 'At least one timing of bet must be selected'),
                otherwise: () => Yup.array().nullable(),
            }),
            alertTime: Yup.string().when(`isAnalyst`, {
                is: () => !isAnalyst,
                then: () => Yup.string().required('Time of alert is required'),
                otherwise: () => Yup.string().nullable(),
            }),
            customerType: Yup.array().when([`newCustomer`, 'customerDetails'], {
                is: (newCustomer, customerDetails) => {
                    const customerData = [newCustomer, ...customerDetails];
                    const data = customerData.map(c => (c.country && c.count) ? true : false);
                    return !isAnalyst || data.includes(true);
                },
                then: () => Yup.array().min(1, 'At least one customer type must be selected'),
                otherwise: () => Yup.array().nullable(),
            }),
            maxBets: Yup.string().when(`isAnalyst`, {
                is: () => !isAnalyst,
                then: () => Yup.string().required('Max Bets is required'),
                otherwise: () => Yup.string().optional(),
            }),
            customerDetails: Yup.array().of(
                Yup.object().shape({
                    count: Yup.number().when(`isAnalyst`, {
                        is: () => !isAnalyst,
                        then: () => Yup.number().typeError('Number of customers must be a number').min(1, 'Number of customers must be at least 1').required('Number of customers is required'),
                        otherwise: () => Yup.number().optional(),
                    }),
                    country: Yup.object().when(`count`, {
                        is: (count) => { return !isAnalyst || count > 0},
                        then: () => Yup.object().required('Country required'),
                        otherwise: () => Yup.object().nullable(),
                    }),
                    state: Yup.string().when('country', {
                        is: (country) => {
                            return displayStatesForSelectedCountries.includes(country?.name);
                        },
                        then: () => Yup.string().required('State required'),
                        otherwise: () => Yup.string().nullable(),
                    }),
                }).test('country-count-validation', { count:'Number of customers must be at least 1'}, function (values) {
                    const { count, country } = values;
                    if (!count && country?.name) {
                      return false; // This will trigger the error message below
                    }
                    return true; // Valid if no errors
                  })
            ),
            newCustomer: Yup.object().shape({
                count: Yup.number().when(`isAnalyst`, {
                    is: () => !isAnalyst,
                    then: () => Yup.number().typeError('Number of customers must be a number').min(1, 'Number of customers must be at least 1').required('Number of customers is required'),
                    otherwise: () => Yup.number().optional(),
                }),
                country: Yup.object().when(`count`, {
                    is: (count) => { return !isAnalyst || count > 0},
                    then: () => Yup.object().required('Country required'),
                    otherwise: () => Yup.object().nullable(),
                }),
                state: Yup.string().when('country', {
                    is: (country) => {
                        return displayStatesForSelectedCountries.includes(country?.name);
                    },
                    then: () => Yup.string().required('State required'),
                    otherwise: () => Yup.string().nullable(),
                }),
            }).test('country-count-validation', { count:'Number of customers must be at least 1'}, function (values) {
                const { count, country } = values;
                if (!count && country?.name) {
                  return false; // This will trigger the error message below
                }
                return true; // Valid if no errors
              }),
            description: Yup.string().when(`isAnalyst`, { // for edit response
                is: () => responseID ? !isAnalyst : false,
                then: () => Yup.string().test(
                    "valid-text",
                    "Enter valid text",
                    (value) => !(getPlainText(value) === 0 && value)
                ).required("Description required").max(descriptionCount,  `Description should be ${descriptionCount} characters or less.`),
                otherwise: () => Yup.string().optional().test("valid-text", "Enter valid text", (value) =>  !(getPlainText(value) === 0 && value)).max(descriptionCount,  `Description should be ${descriptionCount} characters or less.`),
            }),
            attachments: Yup.array()
            .max(maxNumberOfFiles, validationMessages.maxNumberOfFiles)
            .nullable()
            .test('fileType', validationMessages.allFileTypesWithoutImage, (files) => {
                if (!files) return true;
                return files?.every((file) => fileTypesConfig.includes(file.type))
            })
            .test('fileSize', validationMessages.sizeExceed, (files) => {
                if (!files) return true;
                return files?.every((file) => {
                return file.size <= fileUploadLimit.value
                })
            })
            .test('uniqueFileNames', validationMessages.uniqueFileNames, (value) => {
                if (!value || value.length <= 1) return true;
                const fileNames = value.map((file) => file.name);
                const uniqueFileNames = new Set(fileNames);
                return fileNames.length === uniqueFileNames.size;
            })
        }).test('customer-details-validation', 'Number of customers and country are required when the customer type is selected.', function (values) {
              const { customerType, customerDetails, newCustomer } = values;          
              if (customerType?.length > 0) {
                const allCustomers = [newCustomer, ...customerDetails];
                const hasValidCustomer = allCustomers.some(
                  (c) => c?.country || c?.count
                );
                if (!hasValidCustomer && isAnalyst) {
                    return this.createError({
                        path: 'newCustomerDetails', // This targets the specific field
                        message: 'Number of customers and country are required when the customer type is selected.',
                      });
                }
              }
          
              return true;
            }
          );

        // Dynamically add validation rules for event fields
        for (let i = 0; i < events.length; i++) {
            schema = schema.shape({
                [`eventName-${i}`]: Yup.string().required(`Event name is required`),
                [`sport-${i}`]: Yup.object().nullable().required(`Sport is required`),
                [`eSportTitle-${i}`]: Yup.object().when([`sport-${i}`], {
                    is: (sportName) => sportName?.name.toLowerCase() === 'esports' && !responseID,
                    then: () => Yup.object().required('Esports Title is required'),
                    otherwise: () => Yup.object().nullable(),
                }),
                [`league-${i}`]: Yup.mixed().required('League is required'),
                [`tournament-${i}`]: Yup.object().when([`sport-${i}`], {
                    is: (sportName) => sportName?.name?.toLowerCase() === 'tennis',
                    then: () => Yup.object().required('Tournament level is required'),
                    otherwise: () => Yup.object().nullable(),
                }),
                [`location-${i}`]: Yup.object().nullable().required(`Location is required`),
                [`gender-${i}`]: Yup.string().required(`Gender is required`),
                [`dateTime-${i}`]: Yup.date().required(`Date / Time is required`),
                [`turnover-${i}`]: Yup.number().when(`isMember`, {
                    is: () => isMember === true,
                    then: () => Yup.number().required('Turnover must be greater than or equal to 0').min(0, 'Turnover must be greater than or equal to 0'),
                    otherwise: () => Yup.number().nullable(),
                }),
            });

            const isSuspicious = values ? values[`suspicious-${i}`] : events[i]?.suspicious;
            if (isSuspicious && isAnalyst) {
                schema = schema.shape({
                    [`customerLocations-${i}`]: Yup.array().of(
                        Yup.object().shape({
                            count: Yup.number().when(`isAnalyst`, {
                                is: () => !isAnalyst,
                                then: () => Yup.number().typeError('Number of customers must be a number').min(1, 'Number of customers must be at least 1').required('Number of customers is required'),
                                otherwise: () => Yup.number().optional(),
                            }),
                            country: Yup.object().when(`count`, {
                                is: (count) => { return !isAnalyst || count > 0 },
                                then: () => Yup.object().required('Country required'),
                                otherwise: () => Yup.object().nullable(),
                            }),
                            state: Yup.string().when('country', {
                                is: (country) => {
                                    return displayStatesForSelectedCountries.includes(country?.name);
                                },
                                then: () => Yup.string().required('State required'),
                                otherwise: () => Yup.string().nullable(),
                            }),
                        }).test('country-count-validation', { count: 'Number of customers must be at least 1' }, function (val) {
                            const { count, country } = val;
                            if (!count && country?.name) {
                                return false;
                            }
                            return true;
                        })
                    )
                });
            }
        }
        return schema;
    };
    // Get list of all the sports
    const getSportsHandler = useCallback(() => {
        getSports(null, headers, dispatch, response => {
            if (response.result === 'SUCCESS') {
                const responseData = response.data;
                const sports = responseData;
                const result = (sports?.length > 0) ? sports?.sort((a, b) => a.name.localeCompare(b.name)) : [];
                setSports(result);
                if (result.length === 0) {
                    showToast("warn", "No records found");
                }
            } else {
                setSports([]);
                const error = response.error;
                showToast(error.severity, error?.errorMsg || error?.summary);
            }
        })
    }, [dispatch, headers, showToast]);

    // Get list of all the locations
    const getLocationHandler = useCallback(() => {
        getLocations(headers, dispatch, response => {
            if (response.result === 'SUCCESS') {
                const responseData = response.data;
                const locations = responseData;
                const result = (locations?.length > 0) ? locations?.sort((a, b) => a.name.localeCompare(b.name)) : [];
                setLocation(result);
                if (result.length === 0) {
                    showToast("warn", "No records found");
                }
            } else {
                setLocation([]);
                const error = response.error;
                showToast(error.severity, error?.errorMsg || error?.summary);
            }
        })
    }, [dispatch, headers, showToast]);

    // Get list of all the tournament levels
    const getTournmamentLevelsHandler = useCallback(() => {
        getTournamentLevels(null, headers, dispatch, response => {
            if (response.result === 'SUCCESS') {
                const responseData = response.data;
                const tournamentLevel = responseData;
                const result = (tournamentLevel?.length > 0) ? tournamentLevel?.sort((a, b) => a.name.localeCompare(b.name)) : [];
                setTournamentLevel(result);
            } else {
                setTournamentLevel([]);
                const error = response.error;
                showToast(error.severity, error?.errorMsg || error?.summary);
            }
        })
    }, [dispatch, headers, showToast]);


    // Get list of all the eSports Titles
    const getEsportsTitlesHandler = useCallback(() => {
        geteSportsTitles(headers, dispatch, response => {
            if (response.result === 'SUCCESS') {   
                const responseData = response.data;
                const eSportTitles = responseData;
                const result = (eSportTitles?.length > 0) ? eSportTitles?.sort((a, b) => a.name.localeCompare(b.name)) : [];
                seteSportTitles(result);
            } else {
                seteSportTitles([]);
                const error = response.error;
                showToast(error.severity, error?.errorMsg || error?.summary);
            }
        })
    }, [dispatch, headers, showToast]);
   
    // Get list of all the Players
    const getPlayerTags = useCallback(() => {
        getPlayers({ "categories[]": "athleteteam"}, headers, dispatch, (response) => {
            if (response.result === "SUCCESS") {
                setTagListShow(true);
                const responseData = response.data;
                const result = responseData?.length > 0 ? responseData?.sort((a, b) => a.name.localeCompare(b.name)) : [];
                setAthleteTeamTagList(result);
            } else {
                setAthleteTeamTagList([]);
                const error = response.error;
                showToast(error.severity, error?.errorMsg || error?.summary);
            }
        });

        getPlayers({ "categories[]": "market"}, headers, dispatch, (response) => {
            if (response.result === "SUCCESS") {
                setTagListShow(true);
                const responseData = response.data;
                const result = responseData?.length > 0 ? responseData?.sort((a, b) => a.name.localeCompare(b.name)) : [];
                setMarketTagList(result);
            } else {
                setMarketTagList([]);
                const error = response.error;
                showToast(error.severity, error?.errorMsg || error?.summary);
            }
        });
    }, [dispatch, headers, showToast]);
    // Fetch timezones with type
    useEffect(() => {
        const fetchTimeZones = async () => {
            try {
                const tzOptions = getTimeZoneOptions();
                setTimezones(tzOptions);
            } catch (error) {
                console.error("Error fetching time zones:", error);
            } 
        };
        fetchTimeZones();
    }, []);
    useEffect(() => {
        getEsportsTitlesHandler();
        getTournmamentLevelsHandler();
        if (isAnalyst) {
            getPlayerTags();
        }
        getSportsHandler();
        getLocationHandler();
    }, [getSportsHandler, getLocationHandler, getTournmamentLevelsHandler, getPlayerTags, getEsportsTitlesHandler, isAnalyst]);

     // Get all the tournaments based on SportId
     const getTournamenthandler = useCallback((sportId, filterString) => {
        const obj = {
            sportid: sportId,
            filter: filterString.toLowerCase()
        };
        getTournament(obj, headers, dispatch, response => {
            if (response.result === 'SUCCESS') {
                const responseData = response.data;
                const tournaments = responseData;
                setFilteredTournaments(tournaments);
            } else {
                setFilteredTournaments([]);
                const error = response.error;
                showToast(error.severity, error?.errorMsg || error?.summary);
            }
        })
    }, [dispatch, headers, showToast]);


    const handleSubmit = (formData) => {
        let updatedFields = {
            alertID: initialData?.alertID,
        };
        const obj = {
            alertType: formData.alertType.id,
            channel: formData.channel,
            timeOfBet: formData.betTime,
            stateOfBet: formData.betsState,
            maxBets: formData.maxBets,
            timeOfAlert: formData.alertTime,
        };

        if(deletedAttachments.length > 0){
            obj.deletedAttachments = deletedAttachments;
        }
        //
        let customerInfoChanged = false;
        const compareAndUpdate = (key, newValue) => {
            let initialValue

            if(key === 'customerDetails'){
                initialValue = initialData.customerInfo?.customerDetails
            }else if(key === 'customerType'){
                initialValue = initialData.customerInfo?.customerType
            }else{
                initialValue = initialData[key];
            }
            if (!isEqual(newValue, initialValue)) {
                updatedFields[key] = newValue;
                if (key === 'customerDetails' || key === 'customerType') {
                    customerInfoChanged = true;
                }
            }
        };
    
        Object.keys(obj).forEach(key => {
            compareAndUpdate(key, obj[key]);
        });

        let customerDetails = formik.values?.customerDetails?.length ? formik.values?.customerDetails : []
        if( formData?.newCustomer?.count !== ''){
            customerDetails = [...customerDetails, formData?.newCustomer];
        }
        customerDetails =  formatCustomerInfo(customerDetails)
        compareAndUpdate('customerDetails', customerDetails);
        compareAndUpdate('customerType', formData.customerType);
    
        if (customerInfoChanged) {
            updatedFields.customer = {
                customerDetails: customerDetails,
                customerType: formData.customerType
            }
            delete updatedFields?.customerDetails
            delete updatedFields?.customerType
        }
        const eventsData = formatEventData(events);
        if (responseID && isAnalyst) {
            updatedFields['events'] = eventsData;
        } else {
            compareAndUpdate('events', eventsData);
        }
        const refinedObj = updatedFields ? updatedFields : {};
        if(responseID){ // edit response for member.
            refinedObj.responseID = responseID;
            refinedObj.responseType = 'offered and suspicious';
            if('alertID' in refinedObj){
                delete  refinedObj.alertID;
            }
            refinedObj.description = textSanitize(formData.description)
        }
        refinedObj.maxBets = formData.maxBets ?  true : false;
        if(isAnalyst) {
            refinedObj.tags = [...selectedAthleteTeamTags, ...selectedMarketTags];
        }
        const formResponse = new FormData();
        formResponse.append('data', JSON.stringify(refinedObj));
        if (formData.attachments && formData.attachments.length > 0) {
            let newAttachments = formData.attachments
            if(initialData?.fileAttachment?.length>0){
                newAttachments = formData.attachments.filter(attachment => {
                    return !initialData.fileAttachment.some(file => file.key === attachment.key);
                })
            }
            newAttachments?.forEach((file, index) => {
                formResponse.append(`attachments`, file);
            });
        }
       handleNext(formResponse);
    };

    const formik = useFormik({
        initialValues,
        validateOnChange: true,
        enableReinitialize:true,
        validationSchema: () => validationSchema(formik.values),
        onSubmit: () => {
            handleSubmit(formik.values);
        }
    });

    const handleAdd = () => {
        // Use a default empty array if customerDetails is not defined or not an array
        const newCustomers = [...(formik.values?.customerDetails || []), { count: '', country: '' }];
        formik.setFieldValue('customerDetails', newCustomers);
    };
    
    const handleDelete = (index, type = '') => {
        if(!type){
            const updatedCustomers = [...formik.values.customerDetails];
            updatedCustomers?.splice(index, 1);
            formik.setFieldValue('customerDetails', updatedCustomers);
        } else {
           formik.setFieldValue('newCustomer', {count :'', country: ''});
        }
       
    };

    const handleAddCustomerLocation = (eventIndex) => {
        const fieldName = `customerLocations-${eventIndex}`;
        const currentLocations = formik.values[fieldName] || [];
        formik.setFieldValue(fieldName, [...currentLocations, { count: '', country: null }]);
    };

    const handleDeleteCustomerLocation = (eventIndex, locationIndex) => {
        const fieldName = `customerLocations-${eventIndex}`;
        const currentLocations = formik.values[fieldName] || [];
        if (currentLocations.length === 1) {
            formik.setFieldValue(fieldName, [{ count: '', country: null }]);
        } else {
            const updated = [...currentLocations];
            updated.splice(locationIndex, 1);
            formik.setFieldValue(fieldName, updated);
        }
    };
    const formatEventData = (eventsData) => {
        const updatedEvents = eventsData.map((event, index) => {
            let data = {};
            data.eventID = event.eventID;
            data.eventName = event.eventName;
            data.sportName = event.sport.name;
            data.sportID = event.sport.id;
            if(data.sportName.toLowerCase() === 'esports' && event.eSportTitle?.id){
                data.esportsTitleID = event.eSportTitle.id;
                data.esportsTitleName = event.eSportTitle.name;
            }
            if(event.tournament?.id){
                data.tournamentLevelID = event.tournament.id;
                data.tournamentLevelName = event.tournament.name;
            }
            data.tournamentName = event.league?.name;
            data.tournamentID = event.league?.id;
            data.locationID = event.location.id;
            data.locationName = event.location.name;
            data.gender = event.gender;
            data.turnover = event.turnover ? parseInt(event.turnover) : 0;
            if (initialData?.alertStatus === "suspicious" || isAnalyst) {
                data.suspicious = event.suspicious ?? false;
            }
            if (isAnalyst) {
                let customerLocationsRaw = formik.values[`customerLocations-${index}`] || [];

                if (event.suspicious) {
                    const formatted = customerLocationsRaw
                        .filter(c => c.count && c.country)
                        .map(customer => {
                            let cData = {};
                            cData.count = parseInt(customer.count, 10);
                            cData.country = customer.country?.name;
                            if (customer.state) {
                                cData.state = customer.state;
                            }
                            return cData;
                        });
                    data.customerLocations = formatted.filter(f => !!f.country).length > 0 ? formatted : [];
                } else {
                    data.customerLocations = customerLocationsRaw?.filter(f => !!f.country).length > 0 ? null : [];
                }
            }

            const parsedDate = moment(event.dateTime).format('YYYY-MM-DDTHH:mm:ss');
            const dateInUTC = moment.tz(parsedDate, event.timezone).utc().format();
            data.dateTime = dateInUTC;

            return data;
        })

        return updatedEvents;
    }
    const formatCustomerInfo = (customers) => {
        const updatedCustomers = customers?.filter(c => c.count && c.country).map(customer => {
            let data = {}
            data.count = customer?.count;
            data.country = customer?.country?.name;
            if(customer?.state){
                data.state = customer.state
            }
            return data;
        })
        return updatedCustomers;
    }

    const debounceTimeout = useRef(null);

    const handleEventDetailChange = (index, value, property) => {
        const newEvents = [...events];
        newEvents[index][property] = value;
        setEvents(newEvents);
        formik.setFieldValue(`${property}-${index}`, value);
        if (property === 'suspicious') {
            if (value) {
                const currentLocations = formik.values[`customerLocations-${index}`] || [];
                if (currentLocations.length === 0) {
                    formik.setFieldValue(`customerLocations-${index}`, [{ count: '', country: null }]);
                }
            }
        }
        if (property === 'sport') {
            if(value.name ==='eSports'){
                formik.setFieldValue(`locationOptions-${index}`, location)
            }else{
                formik.setFieldValue(`locationOptions-${index}`, location.filter(c => c.name !=="No Country"))
            }
            newEvents[index].tournament = {};   // Reset tournament field
            newEvents[index].league = {};       // Reset tournament level field
            newEvents[index].eSportTitle = {};      
            formik.setFieldValue(`eSportTitle-${index}`, null);
            formik.setFieldValue(`tournament-${index}`, null);
            formik.setFieldValue(`league-${index}`, null);
            formik.setFieldValue(`eSportTitlesOptions-${index}`, [])
            formik.setFieldValue(`tournamentLevelOptions-${index}`, [])

            setEvents(newEvents);

            if (value?.name?.toLowerCase() === 'tennis') {
                formik.setFieldValue(`tournamentLevelOptions-${index}`, tournamentLevel)
            }

            if (value?.name?.toLowerCase() === 'esports') {
                formik.setFieldValue(`eSportTitlesOptions-${index}`, eSportTitles)
            }
        }
        if(property === 'league') {
            if(value?.name.trim() === '') {
                formik.setFieldValue(`league-${index}`, null);
            }  else {
                if (value.name.length > 2) {
                    if (debounceTimeout.current) {
                        clearTimeout(debounceTimeout.current);
                    }
                    debounceTimeout.current = setTimeout(() => {
                        getTournamenthandler(newEvents[index].sport.id,value.name);
                      }, 500);
                }
            }
         } 
    }



    const handleMultiSelect = (e, fieldName) => {
        const { value } = e.target;
        const set = new Set(formik.values[fieldName]);
        if (set.has(value)) {
            set.delete(value);
        } else {
            set.add(value);
        }
        formik.setFieldTouched(fieldName, true);  // Mark as touched
        formik.setFieldValue(fieldName, Array.from(set));
    }

    const handleMaxBets = (value) => {
        setMaxBets(value);
        formik.setFieldValue('maxBets', value)
    }

    const handleAlertTime = (value) => {
        setAlertTime(value);
        formik.setFieldValue('alertTime', value)
    }

    const handleNumOfEventsChange = (e) => {
        const numOfEvents = parseInt(e.target.value);
        if (!isNaN(numOfEvents)) {
            const existingEventsCount = events.length;
            if (numOfEvents > existingEventsCount) {
                // Add new events
                const newEvents = [...events];
                for (let i = existingEventsCount; i < numOfEvents; i++) {
                    newEvents.push({ eventName: '', sport: null });
                }
                setEvents(newEvents);
            } else if (numOfEvents < existingEventsCount) {
                // Remove excess events
                const newEvents = events.slice(0, numOfEvents);
                setEvents(newEvents);
            }
            formik.setFieldValue('numOfEvents', numOfEvents);
        } else {
            setEvents([]);
        }
        formik.handleChange(e);
    };

  

    //  function to filter searched results of autocomplete 
    const search = (event) => {
        const searchTerm = event.query?.toLowerCase();
        return filteredTournaments.filter(tournament =>
            tournament.name?.toLowerCase().includes(searchTerm)
        );
    }

   
    useEffect(() => {
        let updatedInitialValues = {};
        //alertType
        let alertTypeName = alerts.find(item => item.id === initialData.alertType)?.name;
        updatedInitialValues.alertType = { name: alertTypeName, id: initialData.alertType };
        if (type === 'create') {
            updatedInitialValues.channel = [];
            updatedInitialValues.betTime = [];
            updatedInitialValues.betsState = [];
            updatedInitialValues.customerType = [];
            updatedInitialValues.attachments = [];
            updatedInitialValues.customerDetails = [];
            updatedInitialValues.newCustomer = { count: '', country: null };
            updatedInitialValues.description = '';
            setMaxBets('');
            updatedInitialValues.maxBets = '';
        } else {
            updatedInitialValues.channel = initialData.channel?.length ? initialData.channel : [];
            updatedInitialValues.betTime = initialData.timeOfBet?.length ? initialData.timeOfBet : [];
            updatedInitialValues.betsState = initialData.stateOfBet?.length ? initialData.stateOfBet : [];
            // Max bets flag
            const hasMaxBets = !!initialData.maxBets;
            setMaxBets(hasMaxBets);
            updatedInitialValues.maxBets = hasMaxBets;
            // Alert time
            setAlertTime(initialData.timeOfAlert);
            updatedInitialValues.alertTime = initialData.timeOfAlert;
            // Customer info
            updatedInitialValues.customerType = initialData.customerInfo?.customerType ?? [];
            const customerDetails = initialData.customerInfo?.customerDetails ?? [];
            // Map customers and find countryId
            const formattedCustomers = customerDetails?.map(customerItem => {
                const findCountry = location.find(item => item.name === customerItem.country);
                const data = {
                    count: customerItem.count,
                    country: findCountry || { name: customerItem.country }
                };
                if (customerItem.state) {
                    data.state = customerItem.state
                }
                return data
            });

            if (formattedCustomers.length > 0) {
                // Separate the last object from the array
                const lastIndex = formattedCustomers?.length - 1;
                const separatedObject = formattedCustomers[lastIndex];
                const remainingArray = formattedCustomers?.slice(0, lastIndex);
                updatedInitialValues['customerDetails'] = remainingArray;
                updatedInitialValues['newCustomer'] = separatedObject;
            } else {
                updatedInitialValues['customerDetails'] = [];
                updatedInitialValues['newCustomer'] = { count: '', country: null };
            } 
            updatedInitialValues['description'] = initialData.description;
        }
        let eventsData = initialData.events;
        let updatedEvents = [];

        updatedEvents = eventsData.map((event, index) => {
            const eventLocation = location.find(item => item.id === event?.locationID);
            const eventCustomerLocations = event.customerLocations ?? [];
            const formattedCustomerLocations = eventCustomerLocations.map(cl => {
                const findCountry = location.find(item => item.name === cl.country);
                const data = {
                    count: cl.count,
                    country: findCountry || { name: cl.country }
                };
                if (cl.state) {
                    data.state = cl.state;
                }
                return data;
            });

            const formikEventData = {
                [`eventName-${index}`]: event['eventName'],
                [`sport-${index}`]: { name: event['sportName'], id: event['sportID'] },
                [`eSportTitle-${index}`]: event['esportsTitleID'] ? { name: event['esportsTitleName'], id: event['esportsTitleID'] } : null,
                [`league-${index}`]: event['tournamentID'] ? { name: event['tournamentName'], id: event['tournamentID'] } : null,
                [`tournament-${index}`]: event['tournamentLevelID'] ? { name: event['tournamentLevelName'], id: event['tournamentLevelID'] } : null,
                [`eventID-${index}`]: event['eventID'],
                [`dateTime-${index}`]: moment.utc(event.dateTime).local().toDate(),
                [`timezone-${index}`]: event['timezone'],
                [`location-${index}`]: eventLocation?.id ? eventLocation : { name: event['locationName'], id: event['locationID'] },
                [`gender-${index}`]: event['gender'],
                [`turnover-${index}`]: type === 'create' ? '' : event['turnover'],
                [`suspicious-${index}`]: event['suspicious'] ?? false,
                [`customerLocations-${index}`]: formattedCustomerLocations.length > 0
                    ? formattedCustomerLocations
                    : (event.suspicious ? [{ count: '', country: null }] : [])
            };


            if (event['sportName']?.toLowerCase() === 'tennis') {
                formikEventData[`tournamentLevelOptions-${index}`] = tournamentLevel
            } else {
                formikEventData[`tournamentLevelOptions-${index}`] = []
            }

            if (event['sportName']?.toLowerCase() === 'esports') {
                formikEventData[`eSportTitlesOptions-${index}`] = eSportTitles
            } else {
                formikEventData[`eSportTitlesOptions-${index}`] = []
            }

            const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            const updatedEvent = {
                eventName: event['eventName'],
                sport: { name: event['sportName'], id: event['sportID'] },
                eSportTitle: event['esportsTitleID'] ? { name: event['esportsTitleName'], id: event['esportsTitleID'] } : {},
                league: event['tournamentID'] ? { name: event['tournamentName'], id: event['tournamentID'] } : {},
                tournament: event['tournamentLevelID'] ? { name: event['tournamentLevelName'], id: event['tournamentLevelID'] } : {},
                eventID: event['eventID'],
                dateTime: moment.utc(event.dateTime).local().toDate(),
                timezone: timezone,
                location: eventLocation?.id ? eventLocation : { name: event['locationName'], id: event['locationID'] },
                gender: event['gender'],
                turnover: type === 'create' ? '' : event['turnover'],
                suspicious: event['suspicious'] ?? false,
                customerLocations: event['customerLocations'] ?? []
            };

            updatedInitialValues = { ...updatedInitialValues, ...formikEventData };

            return updatedEvent;
        });
        //num of Events
        updatedInitialValues['numOfEvents'] = updatedEvents.length;
        updatedInitialValues['attachments'] = type === 'create' ? [] : initialData?.fileAttachment?.filter((file) => file.owner !== 'IBIA');
        setEvents(updatedEvents);
        setInitialValues(updatedInitialValues);
    }, [alerts, eSportTitles, initialData, location, tournamentLevel, type]);


    // function to delete an attachment 
    const handleDeleteFile = (index) => {
        const deletedFile = formik.values.attachments[index];
        if (deletedFile.key) {
            setDeletedAttachments(prevDeleted => [
                ...prevDeleted,
                { key: deletedFile.key, type: deletedFile.type }
            ]);
        }

        const updatedAttachments = [...formik.values.attachments];
        updatedAttachments?.splice(index, 1);
        formik.setFieldValue('attachments', updatedAttachments);
    };



    const handleAthleteTeamTagChange = (e) => {
        setSelectedAthleteTeamTags(e.value);
    }

    const handleMarketTagChange = (e) => {
        setSelectedMarketTags(e.value);
    }

   
    const getStateOptions = (country) => {
        return location?.find(loc => loc.name === country )?.states
    }

    const handleFileSelect = (event) => {
        if (fileUploadRef.current) {
            fileUploadRef.current.clear();
        }
        const currentAttachments = formik.values.attachments || [];
        const newAttachment = event.files[0];

        const duplicateFiles = currentAttachments.some(existingFile => existingFile.name === newAttachment.name)

        if (duplicateFiles) {
            showToast("warn", validationMessages.uniqueFileNames);
            fileUploadRef.current.clear();
        } else if (currentAttachments?.length + 1 > maxNumberOfFiles) {
            showToast("warn", validationMessages.maxNumberOfFiles);
            fileUploadRef.current.clear();
        } else {
            const updatedAttachments = [...currentAttachments, newAttachment];
            formik.setFieldValue("attachments", updatedAttachments);
        }
    }

    return (
        <>
            {/* Update alert form section starts here */}
            <div className="form-section">
                <form autoComplete="off" onSubmit={formik.handleSubmit}>
                    <div className="form-body">
                        <div className="grid align-items-center">
                            <div className="col-12 mb-2">
                                <div className="flex flex-column gap-2">
                                    <label htmlFor="alertType">Type of alert<span className="important">*</span></label>
                                    <PR.Dropdown
                                        inputId="alertType"
                                        value={formik.values.alertType}
                                        onChange={(e) => {
                                            formik.setFieldValue('alertType', e.value);
                                        }}
                                        onBlur={formik.handleBlur}
                                        name="alertType"
                                        options={alerts}
                                        optionLabel="name"
                                        placeholder="Select type of alert"
                                        className="w-full alert-drop-down"
                                        disabled={true}
                                    />
                                    {formik.errors.alertType && formik.touched.alertType && (
                                        <div className="error">{formik.errors.alertType}</div>
                                    )}
                                </div>
                            </div>
                            <div className="col-12">
                                <div className="flex flex-column form-input gap-2">
                                    <label htmlFor="numOfEvents">Number of events<span className="important">*</span></label>
                                    <PR.InputText
                                        id="numOfEvents"
                                        type="number"
                                        placeholder="Number of Events"
                                        onChange={handleNumOfEventsChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.numOfEvents}
                                        aria-describedby="events-help"
                                        min={0}
                                        max={10}
                                        disabled={true}
                                    />
                                    {formik.errors.numOfEvents && formik.touched.numOfEvents && (
                                        <div className="error">{formik.errors.numOfEvents}</div>
                                    )}
                                </div>
                                {events?.length !== 0 && <div className="events-section">
                                    {/* Event */}
                                    {events?.map((event, index) => (
                                        <div className={`grid align-items-baseline ${(initialData?.alertStatus === "suspicious" && event.suspicious) ? 'suspicious-event-box' : ''}`} key={'event' + index}>
                                            <div className="custom-col">
                                                <span className="number">{index + 1}</span>
                                            </div>
                                            <div className="col">
                                                <div className="flex flex-column form-input gap-2 mb-4">
                                                    <label htmlFor={`eventName-${index}`}>Event name<span className="important">*</span></label>
                                                    <PR.InputText
                                                        type="text"
                                                        aria-describedby="eventname"
                                                        id={`eventName-${index}`}
                                                        name={`eventName-${index}`}
                                                        onBlur={formik.handleBlur}
                                                        value={event.eventName}
                                                        onChange={(e) => handleEventDetailChange(index, e.target.value, 'eventName')}
                                                        disabled={responseID}
                                                    />
                                                    {(formik.touched[`eventName-${index}`] || formik.submitCount > 0) && formik.errors[`eventName-${index}`] && (
                                                        <div className="error">{formik.errors[`eventName-${index}`]}</div>
                                                    )}
                                                </div>
                                                <div className="flex flex-column gap-2 mb-4">
                                                    <label htmlFor={`sport-${index}`}>Select sport<span className="important">*</span></label>
                                                    <PR.Dropdown
                                                        value={event.sport}
                                                        inputId={`sport-${index}`}
                                                        name={`sport-${index}`}
                                                        onChange={(e) => handleEventDetailChange(index, e.value, 'sport')}
                                                        options={sports}
                                                        optionLabel="name"
                                                        placeholder="Select sport"
                                                        filter
                                                        onBlur={formik.handleBlur}
                                                        className="w-full alert-drop-down"
                                                        disabled={responseID}
                                                        resetFilterOnHide
                                                    />
                                                    {(formik.touched[`sport-${index}`] || formik.submitCount > 0) && formik.errors[`sport-${index}`] && (
                                                        <div className="error">{formik.errors[`sport-${index}`]}</div>
                                                    )}
                                                </div>
                                                {event?.sport?.name?.toLowerCase() === 'esports' &&
                                                    <div className="flex flex-column gap-2 mb-4">
                                                        <label htmlFor={`eSportTitle-${index}`}>Esports Title<span className="important">*</span></label>
                                                        <PR.Dropdown
                                                            value={event.eSportTitle}
                                                            inputId={`eSportTitle-${index}`}
                                                            name={`eSportTitle-${index}`}
                                                            onChange={(e) => handleEventDetailChange(index, e.value, 'eSportTitle')}
                                                            options={formik.values[`eSportTitlesOptions-${index}`]}
                                                            optionLabel="name"
                                                            filter
                                                            onBlur={formik.handleBlur}
                                                            placeholder="Select Esports Title"
                                                            className="w-full alert-drop-down"
                                                            disabled={formik.values[`eSportTitlesOptions-${index}`]?.length <= 0 || responseID}
                                                        />
                                                        {(formik.touched[`eSportTitle-${index}`] || formik.submitCount > 0) && formik.errors[`eSportTitle-${index}`] && (
                                                            <div className="error-message">{formik.errors[`eSportTitle-${index}`]}</div>
                                                        )}
                                                    </div>
                                                }
                                                <div className="grid mb-4">
                                                    <div className="col-6">
                                                        <div className="flex flex-column form-input gap-2">
                                                            <label htmlFor={`league-${index}`}>League / Tournament<span className="important">*</span></label>
                                                            <PR.AutoComplete
                                                                inputId={`league-${index}`}
                                                                name={`league-${index}`}
                                                                onBlur={formik.handleBlur}
                                                                dropdownAutoFocus={true}
                                                                value={event.league ? event.league?.name : ''}
                                                                onChange={(e) => {
                                                                    if (typeof e.value === 'string') {
                                                                        handleEventDetailChange(index, { name: e.value }, 'league'); // value is a string
                                                                    } else if (e.value) {
                                                                        handleEventDetailChange(index, e.value, 'league'); //value is an object
                                                                    }
                                                                }}
                                                                aria-describedby="league"
                                                                suggestions={filteredTournaments}
                                                                completeMethod={search}
                                                                itemTemplate={(tournament) => tournament.name}
                                                                className="form-autocomplete"
                                                                panelClassName="editAlert"
                                                                disabled={!event.sport || responseID}
                                                            />
                                                            {(formik.touched[`league-${index}`] || formik.submitCount > 0) && formik.errors[`league-${index}`] && (
                                                                <div className="error">{formik.errors[`league-${index}`]}</div>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="col-6">
                                                        <div className="flex flex-column gap-2">
                                                            <label htmlFor={`tournament-${index}`}>Tournament level {(event.sport?.name?.toLowerCase() === 'tennis') && <span className="important">*</span>}</label>
                                                            <PR.Dropdown
                                                                value={event.tournament}
                                                                inputId={`tournament-${index}`}
                                                                name={`tournament-${index}`}
                                                                onChange={(e) => handleEventDetailChange(index, e.value, 'tournament')}
                                                                options={formik.values[`tournamentLevelOptions-${index}`]}
                                                                optionLabel="name"
                                                                filter
                                                                onBlur={formik.handleBlur}
                                                                placeholder="Select tournament level"
                                                                className="w-full alert-drop-down"
                                                                disabled={formik.values[`tournamentLevelOptions-${index}`]?.length <= 0 || responseID}
                                                            />
                                                            {(formik.touched[`tournament-${index}`] || formik.submitCount > 0) && formik.errors[`tournament-${index}`] && (
                                                                <div className="error">{formik.errors[`tournament-${index}`]}</div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="grid mb-4">
                                                    <div className="col-6">
                                                        <div className="flex flex-column gap-2">
                                                            <label htmlFor={`location-${index}`}>Location of event<span className="important">*</span></label>
                                                            <PR.Dropdown
                                                                value={event.location}
                                                                inputId={`location-${index}`}
                                                                name={`location-${index}`}
                                                                onChange={(e) => handleEventDetailChange(index, e.value, 'location')}
                                                                options={formik.values[`locationOptions-${index}`] || location}
                                                                optionLabel="name"
                                                                onBlur={formik.handleBlur}
                                                                filter
                                                                placeholder="Select event location"
                                                                className="w-full alert-drop-down"
                                                                disabled={responseID}
                                                            />
                                                            {(formik.touched[`location-${index}`] || formik.submitCount > 0) && formik.errors[`location-${index}`] && (
                                                                <div className="error">{formik.errors[`location-${index}`]}</div>
                                                            )}
                                                        </div>
                                                        {initialData?.alertStatus === "suspicious" && (
                                                            <div className={`flex align-items-center status-box mt-2 ${event.suspicious ? 'suspicious-status-box' : ''}`}>
                                                                <PR.Checkbox
                                                                    inputId={`suspicious-${index}`}
                                                                    name={`suspicious-${index}`}
                                                                    onChange={(e) => handleEventDetailChange(index, e.checked, 'suspicious')}
                                                                    checked={!!event.suspicious}
                                                                    disabled={responseID || !isAnalyst}
                                                                />
                                                                <label htmlFor={`suspicious-${index}`} className="cursor-pointer ml-2 font-medium">
                                                                    Suspicious
                                                                </label>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="col-6">
                                                        <div className="flex flex-column gap-2">
                                                            <label htmlFor={`dateTime-${index}`}>Date / Time of event <span style={{color: '#5b5858'}}> (Format: dd/mm/yyyy hh:mm)</span><span className="important">*</span></label>
                                                            <PR.Calendar
                                                                inputId={`dateTime-${index}`}
                                                                name={`dateTime-${index}`}
                                                                placeholder="Select date"
                                                                value={event.dateTime}
                                                                onFocus={() => formik.setFieldTouched(`dateTime-${index}`)}
                                                                onChange={(e) => handleEventDetailChange(index, e.value, 'dateTime')}
                                                                showTime hourFormat="24"
                                                                showIcon iconPos="right"
                                                                className="date-calendar"
                                                                dateFormat="dd/mm/yy"
                                                                disabled={responseID}
                                                                onKeyDown={(e) => {
                                                                    if (e.key === 'Enter') {
                                                                        e.preventDefault(); // Prevent form submission
                                                                    }
                                                                }}
                                                            />
                                                            {(formik.touched[`dateTime-${index}`] || formik.submitCount > 0) && formik.errors[`dateTime-${index}`] && (
                                                                <div className="error">{formik.errors[`dateTime-${index}`]}</div>
                                                            )}
                                                        </div>
                                                        <div className="flex flex-column gap-2 mt-2">
                                                            <PR.Dropdown
                                                                value={event.timezone}
                                                                inputId={`timezone-${index}`}
                                                                name={`timezone-${index}`}
                                                                onChange={(e) => handleEventDetailChange(index, e.value, 'timezone')}
                                                                options={timezones}
                                                                onBlur={formik.handleBlur}
                                                                filter
                                                                placeholder="Select timezone"
                                                                className="w-full alert-drop-down"
                                                                disabled={responseID}
                                                                resetFilterOnHide
                                                            />
                                                            {(formik.touched[`timezone-${index}`] || formik.submitCount > 0) && formik.errors[`timezone-${index}`] && (
                                                                <div className="error-message">{formik.errors[`timezone-${index}`]}</div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                                {initialData?.alertStatus === "suspicious" && (formik.values[`customerLocations-${index}`] || []).length >=1 && (
                                                    <div className="grid mb-4">
                                                        <div className="col-12">
                                                            <div className="flex flex-column gap-2">
                                                                <span className="label">Locations of suspicious customers</span>
                                                                {(formik.values[`customerLocations-${index}`] || []).map((loc, lIdx) => {
                                                                    const countryTouched = formik.touched[`customerLocations-${index}`]?.[lIdx]?.country || formik.submitCount > 0;
                                                                    const countTouched = formik.touched[`customerLocations-${index}`]?.[lIdx]?.count || formik.submitCount > 0;
                                                                    const stateTouched = formik.touched[`customerLocations-${index}`]?.[lIdx]?.state || formik.submitCount > 0;
                                                                    return (
                                                                        <div className="grid align-items-start mb-0" key={lIdx}>
                                                                            <div className="col-3 form-input">
                                                                                <PR.InputText
                                                                                    type="number"
                                                                                    id={`customerLocations-${index}[${lIdx}].count`}
                                                                                    name={`customerLocations-${index}[${lIdx}].count`}
                                                                                    value={formik.values[`customerLocations-${index}`]?.[lIdx]?.count ?? ''}
                                                                                    onChange={formik.handleChange}
                                                                                    onBlur={formik.handleBlur}
                                                                                    placeholder="No. of customers"
                                                                                    min={0}
                                                                                    disabled={responseID || !event.suspicious}
                                                                                />
                                                                                {countTouched && formik.errors[`customerLocations-${index}`]?.[lIdx]?.count && (
                                                                                    <div className="error">{formik.errors[`customerLocations-${index}`][lIdx].count}</div>
                                                                                )}
                                                                            </div>
                                                                            <div className="col-4">
                                                                                <PR.Dropdown
                                                                                    inputId={`customerLocations-${index}[${lIdx}].country`}
                                                                                    name={`customerLocations-${index}[${lIdx}].country`}
                                                                                    value={formik.values[`customerLocations-${index}`]?.[lIdx]?.country || ''}
                                                                                    onChange={(e) => {
                                                                                        formik.setFieldValue(`customerLocations-${index}[${lIdx}].state`, '');
                                                                                        formik.setFieldValue(`customerLocations-${index}[${lIdx}].country`, e.value);
                                                                                    }}
                                                                                    onBlur={formik.handleBlur}
                                                                                    options={location}
                                                                                    optionLabel="name"
                                                                                    placeholder="Select country"
                                                                                    filter
                                                                                    resetFilterOnHide
                                                                                    className="w-full alert-drop-down mb-1"
                                                                                    disabled={responseID || !event.suspicious}
                                                                                />
                                                                                {countryTouched && formik.errors[`customerLocations-${index}`]?.[lIdx]?.country && (
                                                                                    <div className="error">{formik.errors[`customerLocations-${index}`][lIdx].country}</div>
                                                                                )}
                                                                                {displayStatesForSelectedCountries.includes(formik.values[`customerLocations-${index}`]?.[lIdx]?.country?.name) && (
                                                                                    <>
                                                                                        <PR.Dropdown
                                                                                            inputId={`customerLocations-${index}[${lIdx}].state`}
                                                                                            name={`customerLocations-${index}[${lIdx}].state`}
                                                                                            value={formik.values[`customerLocations-${index}`]?.[lIdx]?.state || ''}
                                                                                            onChange={(e) => {
                                                                                                formik.setFieldValue(`customerLocations-${index}[${lIdx}].state`, e.value);
                                                                                            }}
                                                                                            onBlur={formik.handleBlur}
                                                                                            options={getStateOptions(formik.values[`customerLocations-${index}`]?.[lIdx]?.country?.name)}
                                                                                            placeholder="Select state"
                                                                                            filter
                                                                                            resetFilterOnHide
                                                                                            className="w-full alert-drop-down"
                                                                                            disabled={responseID || !event.suspicious}
                                                                                        />
                                                                                        {stateTouched && formik.errors[`customerLocations-${index}`]?.[lIdx]?.state && (
                                                                                            <div className="error">{formik.errors[`customerLocations-${index}`][lIdx].state}</div>
                                                                                        )}
                                                                                    </>
                                                                                )}
                                                                            </div>
                                                                            <div className="col-5 form-input">
                                                                                <div className="displayGrid">
                                                                                    {((formik.values[`customerLocations-${index}`]?.length > 1 || formik.values[`customerLocations-${index}`]?.[0]?.country || formik.values[`customerLocations-${index}`]?.[0]?.count >= 1) && !responseID) && (
                                                                                        <PR.Button
                                                                                            label="Remove"
                                                                                            type="button"
                                                                                            icon="pi pi-minus"
                                                                                            className="addmore-button"
                                                                                            onClick={() => handleDeleteCustomerLocation(index, lIdx)}
                                                                                            disabled={!event.suspicious}
                                                                                        />
                                                                                    )}
                                                                                    {lIdx === 0 && !responseID && (
                                                                                        <PR.Button
                                                                                            label="Add More"
                                                                                            type="button"
                                                                                            icon="pi pi-plus"
                                                                                            className="addmore-button"
                                                                                            onClick={() => handleAddCustomerLocation(index)}
                                                                                            disabled={!event.suspicious}
                                                                                        />
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                                <div className="grid mb-4">
                                                    <div className="col-12">
                                                        <div className="flex flex-column gap-2">
                                                            <span className="label">Gender<span className="important">*</span></span>
                                                            <div className="card flex mt-1 gap-3">
                                                                {gender.map((option) => (
                                                                    <div className="flex flex-wrap gap-2" key={'gender' + option.id}>
                                                                        <div className="flex align-items-center">
                                                                            <PR.RadioButton
                                                                                disabled={(isMember || responseID) ? true : false}
                                                                                inputId={`gender-${option.id}${index}`}
                                                                                name="gender"
                                                                                value={option.id}
                                                                                onChange={(e) => handleEventDetailChange(index, e.value, 'gender')}
                                                                                checked={event.gender === option.id}
                                                                            />
                                                                            <label htmlFor={`gender-${option.id}${index}`} className="ml-2">{option.name}</label>
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                            {(formik.touched[`gender-${index}`] || formik.submitCount > 0) && formik.errors[`gender-${index}`] && (
                                                                <div className="error">{formik.errors[`gender-${index}`]}</div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="grid mb-0">
                                                    <div className="col-12">
                                                        <div className="flex flex-column form-input gap-2">
                                                            <label htmlFor={`turnover-${index}`}>How much did the suspicious turnover account for as a percentage of the event as a whole, if available?{ isMember && <span className="important">*</span>}</label>
                                                            <PR.InputText
                                                                id={`turnover-${index}`}
                                                                name={`turnover-${index}`}
                                                                value={event.turnover !== undefined && event.turnover !== null ? event.turnover : ''}
                                                                onChange={(e) => {
                                                                    handleNumericInput(e);
                                                                    handleEventDetailChange(index, e.target.value, 'turnover');
                                                                }}
                                                                type="text"
                                                                aria-describedby="eventname"
                                                            />
                                                              {(formik.touched[`turnover-${index}`] || formik.submitCount > 0) && formik.errors[`turnover-${index}`] && (
                                                                <div className="error">{formik.errors[`turnover-${index}`]}</div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                                <hr className="mt-4 mb-4" />
                                            </div>
                                        </div>
                                    ))}
                                </div>}
                            </div>
                            <div className="col-12 mb-3">
                                <div className="flex flex-column gap-2">
                                    <span className="label">Channel {!isAnalyst && <span className="important">*</span>}</span>
                                    <div className="flex flex-wrap gap-3 mt-1">
                                        {channels.map((channel) => (
                                            <div className="flex align-items-center" key={'channel' + channel.id}>
                                                <PR.Checkbox
                                                    inputId={channel.id}
                                                    value={channel.id}
                                                    onChange={(e) => handleMultiSelect(e, 'channel')}
                                                    checked={formik.values.channel?.includes(channel.id)}></PR.Checkbox>
                                                <label htmlFor={channel.id} className="ml-2 font-medium">{channel.name}</label>
                                            </div>
                                        ))}
                                    </div>
                                    {formik.errors.channel && (formik.touched.channel || formik.submitCount > 0) && (
                                        <div className="error">{formik.errors.channel}</div>
                                    )}
                                </div>
                            </div>
                            <div className="col-12 mb-3">
                                <div className="flex flex-column gap-2">
                                    <span className="label">Time of bet {!isAnalyst && <span className="important">*</span>}</span>
                                    <div className="flex flex-wrap gap-3 mt-1">
                                        {betTimeOptions.map((option) => (
                                            <div className="flex align-items-center" key={'betTime' + option.id}>
                                                <PR.Checkbox
                                                    inputId={option.id}
                                                    value={option.id}
                                                    onChange={(e) => handleMultiSelect(e, 'betTime')}
                                                    checked={formik.values.betTime?.includes(option.id)}></PR.Checkbox>
                                                <label htmlFor={option.id} className="ml-2 font-medium">{option.name}</label>
                                            </div>
                                        ))}
                                    </div>
                                    {formik.errors.betTime && (formik.touched.betTime || formik.submitCount > 0) && (
                                        <div className="error">{formik.errors.betTime}</div>
                                    )}
                                </div>
                            </div>
                            <div className="col-12 mb-3">
                                <div className="flex flex-column gap-2">
                                    <span className="label">State of the bets {!isAnalyst && <span className="important">*</span>}</span>
                                    <div className="flex flex-wrap gap-3 mt-1">
                                        {betsStateOptions.map((option) => (
                                            <div className="flex align-items-center" key={'betState' + option.id}>
                                                <PR.Checkbox
                                                    inputId={option.id}
                                                    value={option.id}
                                                    onChange={(e) => handleMultiSelect(e, 'betsState')}
                                                    checked={formik.values.betsState?.includes(option.id)}></PR.Checkbox>
                                                <label htmlFor={option.id} className="ml-2 font-medium">{option.name}</label>
                                            </div>
                                        ))}
                                    </div>
                                    {formik.errors.betsState && (formik.touched.betsState || formik.submitCount > 0) && (
                                        <div className="error">{formik.errors.betsState}</div>
                                    )}
                                </div>
                            </div>
                            <div className="col-12">
                                <div className="flex flex-column gap-2">
                                    <span className="label">Max bets {!isAnalyst && <span className="important">*</span>}</span>
                                    <div className="card flex mt-1 gap-3">
                                        {confirmOptions.map((option) => (
                                            <div className="flex flex-wrap gap-2" key={'confirmOption' + option.id}>
                                                <div className="flex align-items-center">
                                                    <PR.RadioButton
                                                        inputId={`bet-yes-${option.id}`}
                                                        name="bets"
                                                        value={option.id}
                                                        onChange={(e) => handleMaxBets(e.value)}
                                                        checked={maxBets === option.id}
                                                    />
                                                    <label htmlFor={`bet-yes-${option.id}`} className="ml-2 font-medium">{option.name}</label>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    {formik.errors.maxBets && (formik.touched.maxBets || formik.submitCount > 0) && (
                                        <div className="error">{formik.errors.maxBets}</div>
                                    )}
                                </div>
                            </div>
                            <div className="col-12">
                                <div className="flex flex-column gap-2">
                                    <span className="label">When is the alert being raised?{!isAnalyst && <span className="important">*</span>}</span>
                                    <div className="card flex mt-1 gap-3">
                                        {alertsRaised.map((option) => (
                                            <div className="flex flex-wrap gap-2" key={'alertsRaised' + option.id}>
                                                <div className="flex align-items-center">
                                                    <PR.RadioButton
                                                        inputId={`pre-event-${option.id}`}
                                                        name="raised"
                                                        value={option.id}
                                                        onChange={(e) => handleAlertTime(e.value)}
                                                        checked={alertTime === option.id}
                                                    />
                                                    <label htmlFor={`pre-event-${option.id}`} className="ml-2 font-medium">{option.name}</label>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    {formik.errors.alertTime && (formik.touched.alertTime || formik.submitCount > 0) && (
                                        <div className="error">{formik.errors.alertTime}</div>
                                    )}
                                </div>
                            </div>
                            {(isAnalyst && tagListShow && !responseID) && 
                                <>
                                    <div className="col-12">
                                        <div className="flex flex-column gap-2">
                                            <label htmlFor="athleteTeamTag">Suspicious Athlete/Team Tags</label>
                                            <div className="card flex mt-1 gap-3">
                                                <PR.MultiSelect 
                                                    inputId="athleteTeamTag"
                                                    filter 
                                                    resetFilterOnHide
                                                    value={selectedAthleteTeamTags} 
                                                    onChange={handleAthleteTeamTagChange} 
                                                    options={athleteTeamTagList} 
                                                    optionLabel="name"
                                                    placeholder="Select Tags"
                                                    showSelectAll
                                                    className="w-full alert-drop-down"
                                                    display="chip"
                                                    dataKey="id"
                                                    pt={{
                                                        checkboxContainer: {
                                                            onClick: (e) => {
                                                                e.stopPropagation();
                                                                const parent = e.target?.parentNode;
                                                                if (parent && typeof parent.click === 'function') {
                                                                    parent.click();
                                                                }
                                                            },
                                                        },
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-12">
                                        <div className="flex flex-column gap-2">
                                            <label htmlFor="marketTag">Suspicious Market Tags</label>
                                            <div className="card flex mt-1 gap-3">
                                                <PR.MultiSelect 
                                                    inputId="marketTag"
                                                    filter 
                                                    resetFilterOnHide
                                                    value={selectedMarketTags} 
                                                    onChange={handleMarketTagChange} 
                                                    options={marketTagList} 
                                                    optionLabel="name"
                                                    placeholder="Select Tags"
                                                    showSelectAll
                                                    className="w-full alert-drop-down"
                                                    display="chip"
                                                    dataKey="id"
                                                    pt={{
                                                        checkboxContainer: {
                                                            onClick: (e) => {
                                                                e.stopPropagation();
                                                                const parent = e.target?.parentNode;
                                                                if (parent && typeof parent.click === 'function') {
                                                                    parent.click();
                                                                }
                                                            },
                                                        },
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </>
                            }
                            <div className="col-12">
                                <p className="customer_info">CUSTOMER INFO</p>
                            </div>
                            <div className="col-12 mb-3">
                                <div className="flex flex-column gap-2">
                                    <span className="label">Type of customers {!isAnalyst && <span className="important">*</span>}</span>
                                    <div className="flex flex-wrap gap-3 mt-1">
                                        {customerTypeOptions.map((option) => (
                                            <div className="flex align-items-center" key={'customerType' + option.id}>
                                                <PR.Checkbox
                                                    inputId={option.id}
                                                    value={option.id}
                                                    onChange={(e) => handleMultiSelect(e, 'customerType')}
                                                    checked={formik.values.customerType?.includes(option.id)}></PR.Checkbox>
                                                <label htmlFor={option.id} className="ml-2 font-medium capitalize-first">{option.name}</label>
                                            </div>
                                        ))}
                                    </div>
                                    {formik.errors.customerType && (formik.touched.customerType || formik.submitCount > 0) && (
                                        <div className="error">{formik.errors.customerType}</div>
                                    )}
                                </div>
                            </div>
                            <div className="col-12">
                                <div className="flex flex-column gap-2">
                                    <label htmlFor="newCustomer.count">Number of customers involved {!isAnalyst && <span className="important">*</span>}</label>
                                    {/* Add More Section */}
                                    <div className="grid">
                                        <div className="col-3 form-input">
                                            <PR.InputText
                                                type="number"
                                                id="newCustomer.count"
                                                name="newCustomer.count"
                                                value={formik.values.newCustomer?.count}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                placeholder="No. of customers"
                                                aria-describedby="events-help"
                                                min={0}
                                            />
                                            {formik.errors.newCustomer && (formik.touched.newCustomer || formik.submitCount > 0) && (
                                                <div className="error mt-1">{formik.errors.newCustomer.count}</div>
                                            )}
                                        </div>
                                        <div className="col-4">
                                            <PR.Dropdown
                                                inputId="newCustomer.country"
                                                name="newCustomer.country"
                                                value={formik.values.newCustomer?.country || ''}
                                                onChange= {(e) => {
                                                    formik.setFieldValue(`newCustomer.state`, '');
                                                    formik.handleChange(e);
                                                }}
                                                onBlur={formik.handleBlur}
                                                options={location} 
                                                optionLabel="name"
                                                placeholder="Select country"
                                                filter
                                                resetFilterOnHide
                                                className="w-full alert-drop-down mb-1"
                                            />
                                            {formik.errors.newCustomer && (formik.touched.newCustomer || formik.submitCount > 0) && (
                                                <div className="error-message mt-1">{formik.errors.newCustomer.country}</div>
                                            )}
                                            {displayStatesForSelectedCountries.includes(formik.values.newCustomer?.country?.name) && <PR.Dropdown
                                                inputId="newCustomer.state"
                                                name="newCustomer.state"
                                                value={formik.values.newCustomer?.state || ''}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                options={getStateOptions(formik.values.newCustomer?.country.name)}
                                                placeholder="Select state"
                                                filter
                                                resetFilterOnHide
                                                className="w-full alert-drop-down"
                                            />}
                                            {formik.errors.newCustomer && (formik.touched.newCustomer || formik.submitCount > 0) && (
                                                <div className="error-message mt-1">{formik.errors.newCustomer.state}</div>
                                            )}
                                        </div>
                                        <div className="col-5 form-input ">
                                            <div className="displayGrid">
                                               
                                                {((formik.values.newCustomer?.country || formik.values.newCustomer?.count) && isAnalyst) &&
                                                    <PR.Button label="Remove" type="button" icon="pi pi-minus" className="addmore-button"  onClick={() => handleDelete(null, 'newCustomer')} />
                                                }
                                                 <PR.Button label="Add More" type="button" icon="pi pi-plus" className="addmore-button" onClick={handleAdd} />
                                            </div>
                                        </div>
                                        
                                        { formik.errors?.newCustomerDetails &&<div className="error-message mt-1">{formik.errors?.newCustomerDetails}</div>}
                                    </div>
                                    {formik.values.customerDetails?.map((customer, index) => {
                                        return (
                                            <div className="grid" key={'customer' + index}>
                                                <div className="col-3 form-input">
                                                    <PR.InputText
                                                        id={`customerDetails[${index}].count`}
                                                        name={`customerDetails[${index}].count`}
                                                        type="number"
                                                        value={formik.values.customerDetails[index]?.count}
                                                        onChange={formik.handleChange}
                                                        onBlur={formik.handleBlur}
                                                        placeholder="No. of customers"
                                                        aria-describedby="events-help"
                                                        min={0}
                                                    />
                                                    {formik.touched.customerDetails && formik.touched.customerDetails[index] && formik.errors.customerDetails && formik.errors.customerDetails[index] && formik.errors.customerDetails[index].count && (
                                                        <div className="error mt-1">{formik.errors.customerDetails[index].count}</div>
                                                    )}
                                                </div>
                                                <div className="col-4">
                                                    <PR.Dropdown
                                                        inputId={`customerDetails[${index}].country`}
                                                        name={`customerDetails[${index}].country`}
                                                        value={formik.values.customerDetails[index].country}
                                                        onChange={(e) => {
                                                            formik.setFieldValue(`customerDetails[${index}].state`, '');
                                                            formik.handleChange(e);
                                                        }}
                                                        onBlur={formik.handleBlur}
                                                        options={location}
                                                        optionLabel="name"
                                                        placeholder="Select country"
                                                        filter
                                                        resetFilterOnHide
                                                        className="w-full alert-drop-down mb-1"
                                                    />
                                                    {formik.touched.customerDetails && formik.touched.customerDetails[index] && formik.errors.customerDetails && formik.errors.customerDetails[index] && formik.errors.customerDetails[index].country && (
                                                        <div className="error-message mt-1">{formik.errors.customerDetails[index].country}</div>
                                                    )}
                                                     {displayStatesForSelectedCountries.includes(formik.values.customerDetails[index].country.name) &&
                                                        <PR.Dropdown
                                                        inputId={`customerDetails[${index}].state`}
                                                        name={`customerDetails[${index}].state`}
                                                        value={formik.values.customerDetails[index]?.state}
                                                        onChange={formik.handleChange}
                                                        onBlur={formik.handleBlur}
                                                        options={getStateOptions(formik.values.customerDetails[index].country.name)}
                                                        placeholder="Select state"
                                                        filter
                                                        resetFilterOnHide
                                                        className="w-full alert-drop-down"
                                                    />
                                                    }
                                                    {formik.touched.customerDetails && formik.touched.customerDetails[index] && formik.errors.customerDetails && formik.errors.customerDetails[index] && formik.errors.customerDetails[index].state && (
                                                        <div className="error-message mt-1">{formik.errors.customerDetails[index].state}</div>
                                                    )}
                                                </div>
                                                <div className="col-5 form-input">
                                                    <div className="displayGrid">
                                                        <PR.Button
                                                            label="Remove"
                                                            type="button"
                                                            icon="pi pi-minus"
                                                            className="addmore-button"
                                                            onClick={() => handleDelete(index)}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        )
                                    })}

                                    {/* Add More Section */}
                                </div>
                            </div>
                            {responseID &&
                                <div className="col-12 mb-2">
                                    <div className="flex flex-column gap-2">
                                        <label htmlFor="description">Description of suspicious event  {!isAnalyst && <span className="important">*</span>}</label>
                                        <div className="card flex justify-content-center">
                                            <PR.InputTextarea
                                                id="description"
                                                name="description"
                                                value={formik.values.description}
                                                onChange={(e) => {
                                                    const newValue = e.target.value;
                                                    formik.setFieldValue('description', newValue);
                                                }}
                                                onPaste={(e) => {
                                                    e.preventDefault();
                                                    const pastedData = e.clipboardData.getData('text');
                                                    const input = e.target;
                                                    const { selectionStart, selectionEnd, value } = input;
                                                    const newDescription = value.slice(0, selectionStart) + pastedData + value.slice(selectionEnd);
                                                    formik.setFieldValue('description', newDescription);
                                                    setTimeout(() => {
                                                        input.setSelectionRange(selectionStart + pastedData?.length, selectionStart + pastedData?.length);
                                                    }, 0);
                                                }}
                                                onBlur={formik.handleBlur}
                                                rows={8} cols={30}
                                                className="description-textarea"
                                            />
                                        </div>
                                        {formik.errors.description && (formik.touched.description || formik.submitCount > 0) && (
                                            <div className="error">{formik.errors.description}</div>
                                        )}
                                    </div>
                                </div>
                            }
                            <div className="col-7">
                                <div className="flex flex-column gap-2">
                                    <span htmlFor="label" className="label-name">Attach Files</span>
                                    <div className="card file-upload flex align-items-center">
                                        <PR.FileUpload
                                            auto
                                            mode="advanced"
                                            name="attachments"
                                            accept={aletFormForValidation?.toString()}
                                            chooseLabel="Choose File"
                                            ref={fileUploadRef}
                                            onSelect={handleFileSelect}
                                            onFocus={() => formik.setFieldTouched(`attachments`)}
                                            customUpload
                                        />
                                        <span className="ml-2"> <span className="block">(PDF, DOCX, XLSX)</span>{validationMessages.sizeExceed}</span>
                                    </div>
                                    {formik.values.attachments && (
                                        <div className="uploaded-file-container flex flex-column gap-2">
                                            {formik.values.attachments?.map((attachment, index) => (
                                                <div className="uploaded-files" key={index}>
                                                    {trimFileName(attachment.name)}
                                                    <i className="pi pi-trash" onClick={() => handleDeleteFile(index)} />
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    {formik.errors.attachments && (formik.touched.attachments || formik.submitCount > 0) && (
                                        <div className="error-message" style={{ color: "red" }}>
                                            {Array.isArray(formik.errors.attachments) ? (
                                                Array.from(new Set(formik.errors?.attachments))?.map((error, index) => (
                                                    <div key={index}>{error}</div>
                                                ))
                                            ) : (
                                                <div>{formik.errors.attachments}</div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="error-message">{showError}</div>
                        <ErrorMessage id={errorId} />
                        <div className="form-footer flex align-items-center justify-content-end gap-3 mt-2">
                            <PR.Button
                                label={backLabel}
                                type="button"
                                onClick={handleBack}
                                disabled={alertLoading}
                                className="cancel-button"
                            />
                            <PR.Button
                                label={nextLabel}
                                disabled={!(formik.dirty || hasTagsChanged) || showError || alertLoading}
                                className={`create-alert-button ${isAnalyst ? 'analyst-update' : ''}`}
                                loading={alertLoading}
                                type="submit"
                            />
                        </div>
                    </div>
                </form>
            </div>
            {/* Update alert form section ends here */}
        </>
    );
};

export default EditAlertForm;